import { useContext, useState } from "react";
import { Container, StyledButton } from "../../../components/styled/styled-components";
import Toast from "../../../components/Toast/Toast";
import { AlertType } from "../../../interfaces/enums";
import { AlertContent, ProductDescription } from "../../../interfaces/interfaces";
import { Brand } from "../../../interfaces/types";
import { Context, IContext } from "../../../context/ContextApp";
import { subdomainTypes } from "../../../context/availableSubdomains";
import Icon from "../../../components/Icon/icon";
import { dahliaShirts } from "./data";

interface Props {
  stylesheetLink?: string;
  brand: Brand;
  slug: string;
  name: string;
  description?: ProductDescription;
}

const ProductLink = ({ stylesheetLink, brand, slug, name, description }: Props) => {
  const [toast, setToast] = useState<AlertContent | null>(null);
  const { appBranch }: IContext = useContext(Context);
  const productLink = generateProductLink(brand, slug, name, appBranch);

  const copyLinkToClipboard = async () => {
    if (!productLink) return;
    try {
      await navigator.clipboard.writeText(productLink);

      setToast({
        message: "Copied link to clipboard!",
        type: AlertType.Success,
      });
    } catch (error) {
      setToast({
        message: "Could not copy link to clipboard.",
        type: AlertType.Error,
      });
    }
  };

  return (
    <>
      {brand && slug && appBranch && (
        <>
          <Container row jcsb margin="0.5em 0" style={{ width: "100%" }}>
            {description?.product_detail_message && (
              <small style={{ display: "block", width: "100%", color: "red" }}>
                <u>{description.product_detail_message}</u>
              </small>
            )}
            <h6 style={{ display: "block", width: "100%" }}>Website links:</h6>
            {dahliaShirts.includes(slug) && (
              <a
                style={{ width: "48%" }}
                href="https://cdn.fashionbiz.com/EcoVero.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                <StyledButton style={{ width: "100%" }}>
                  <Container row jcsa aic>
                    EcoVera
                  </Container>
                </StyledButton>
              </a>
            )}
            {stylesheetLink ? (
              <a
                style={{ width: "100%", marginBottom: "1rem" }}
                href={stylesheetLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                <StyledButton style={{ width: "100%" }}>
                  <Container row jcsa aic>
                    Open Stylesheet
                    <Icon name="ExternalLink" />
                  </Container>
                </StyledButton>
              </a>
            ) : (
              <button
                style={{
                  width: "100%",
                  marginBottom: "1rem",
                  backgroundColor: "#808080",
                  padding: "0.5rem 0",
                  borderRadius: "4px",
                  color: "#FFFFFF",
                  border: "transparent",
                }}
              >
                <Container row jcsa aic>
                  No Stylesheet Exists
                </Container>
              </button>
            )}
            {productLink && (
              <>
                <a
                  style={{ width: "48%" }}
                  href={productLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <StyledButton style={{ width: "100%" }}>
                    <Container row jcsa aic>
                      View
                      <Icon name="ExternalLink" />
                    </Container>
                  </StyledButton>
                </a>
                <StyledButton style={{ width: "48%" }} onClick={() => copyLinkToClipboard()}>
                  <Container row jcsa aic>
                    Copy
                    <Icon name="Clipboard" />
                  </Container>
                </StyledButton>
              </>
            )}
          </Container>
          <Toast content={toast} onClick={() => setToast(null)} />
        </>
      )}
    </>
  );
};

const generateProductLink = (
  brand: Brand,
  slug: string,
  name: string,
  appBranch: subdomainTypes | "",
) => {
  const countryMap: Record<subdomainTypes, string> = {
    [subdomainTypes.NZ]: "fashionbiz.co.nz",
    [subdomainTypes.AU]: "fashionbiz.com.au",
    [subdomainTypes.CA]: "fashionbiz.ca",
  };

  const domain = appBranch && countryMap[appBranch];

  const url = `https://www.${domain}/product/${slug}`;

  switch (brand) {
    case "syzmik":
      if (appBranch !== subdomainTypes.CA) {
        const syzmikURL =
          appBranch === subdomainTypes.AU ? `https://www.syzmik.com` : `https://www.syzmik.co.nz`;
        return `${syzmikURL}/product/${slug}`;
      }
      return url;
    case "inaura":
      const inauraURL =
        appBranch === subdomainTypes.AU ? `https://inaura.com.au` : `https://inaura.co.nz`;
      return `${inauraURL}/products/${name.replaceAll(" ", "-")}`;
    default:
      return url;
  }
};

export default ProductLink;
